/**
 * /redeem — Magic-link landing page for gift redemption.
 *
 * The recipient email links to /redeem?token=xxx. From here we either:
 *   a) Apply the gift (if the user is signed in) by calling gift-redeem
 *      with `Accept: application/json`, then route to the right page based
 *      on the JSON result (welcome-gift / already-redeemed / cannot-stack /
 *      redeem-expired).
 *   b) Stash the token in sessionStorage and bounce to /auth?gift_token=...
 *      so App.tsx's post-auth handler finishes the redemption after sign-in.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { PublicShell } from "@/components/PublicShell";

type RedeemResult =
  | { result: "redeemed"; tier: string }
  | { result: "already_redeemed" }
  | { result: "expired" }
  | { result: "cannot_stack"; current: string; gift: string }
  | { result: "needs_auth" };

const GIFT_TOKEN_STORAGE_KEY = "mori_gift_token";

export default function RedeemPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const token = params.get("token");
  const [errorState, setErrorState] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;

    if (!token) {
      navigate("/redeem-expired", { replace: true });
      return;
    }

    if (!user) {
      sessionStorage.setItem(GIFT_TOKEN_STORAGE_KEY, token);
      navigate(`/auth?gift_token=${encodeURIComponent(token)}`, { replace: true });
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!supabaseUrl) {
      setErrorState("This app is misconfigured — please contact hello@meetmori.app.");
      return;
    }

    let cancelled = false;

    void (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData.session?.access_token;
      if (!jwt) {
        if (cancelled) return;
        sessionStorage.setItem(GIFT_TOKEN_STORAGE_KEY, token);
        navigate(`/auth?gift_token=${encodeURIComponent(token)}`, { replace: true });
        return;
      }

      try {
        const res = await fetch(
          `${supabaseUrl}/functions/v1/gift-redeem?token=${encodeURIComponent(token)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwt}`,
              Accept: "application/json",
            },
          },
        );

        if (!res.ok) throw new Error(`Redeem failed (${res.status})`);
        const body = (await res.json()) as RedeemResult;
        if (cancelled) return;

        switch (body.result) {
          case "redeemed":
            navigate(`/welcome-gift?tier=${encodeURIComponent(body.tier)}`, { replace: true });
            return;
          case "already_redeemed":
            navigate("/gift/already-redeemed", { replace: true });
            return;
          case "expired":
            navigate("/redeem-expired", { replace: true });
            return;
          case "cannot_stack":
            navigate(
              `/gift/cannot-stack?current=${encodeURIComponent(body.current)}&gift=${encodeURIComponent(body.gift)}`,
              { replace: true },
            );
            return;
          case "needs_auth":
            sessionStorage.setItem(GIFT_TOKEN_STORAGE_KEY, token);
            navigate(`/auth?gift_token=${encodeURIComponent(token)}`, { replace: true });
            return;
          default:
            setErrorState("Unexpected response — please try again.");
        }
      } catch {
        if (cancelled) return;
        setErrorState("We couldn't redeem your gift right now — please try again.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, token, navigate]);

  return (
    <PublicShell>
      <div className="flex flex-col items-center justify-center flex-1 px-6 py-16 text-center">
        <span className="text-5xl mb-6 block">🌰</span>
        {errorState ? (
          <>
            <h1 className="text-2xl font-semibold text-foreground mb-4 leading-snug">
              We hit a snag
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed max-w-sm">
              {errorState}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-foreground mb-4 leading-snug">
              Unwrapping your gift…
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed max-w-sm">
              Mori is checking the satchel.
            </p>
          </>
        )}
      </div>
    </PublicShell>
  );
}
